import { useState } from "react";
import { X, CalendarDays, CheckCircle2 } from "lucide-react";
import { EcMark } from "@/components/icons/EcMark";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import WalkthroughBookingDialog from "@/components/booking/WalkthroughBookingDialog";

interface WalkthroughPopupProps {
  onClose: () => void;
}

export function WalkthroughPopup({ onClose }: WalkthroughPopupProps) {
  const { language } = useLanguage();
  const [bookingOpen, setBookingOpen] = useState(false);
  const isEl = language === "el";
  const t = (el: string, en: string) => (isEl ? el : en);

  return (
    <div className="relative">
      {/* Decorative gradient header */}
      <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-br from-primary/20 via-primary/5 to-transparent pointer-events-none" />
      <div className="absolute -top-24 -right-24 h-64 w-64 rounded-full bg-primary/20 blur-3xl pointer-events-none" />

      <button
        onClick={onClose}
        aria-label="Close"
        className="absolute top-4 right-4 z-10 p-2 rounded-full bg-background/80 backdrop-blur hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
      >
        <X className="h-4 w-4" />
      </button>

      <div className="relative p-8 md:p-12 flex flex-col items-center text-center">
        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary/15 text-primary rounded-full text-xs font-semibold tracking-wide uppercase mb-5">
          <EcMark className="h-3.5 w-3.5" />
          {t("Δωρεάν Στρατηγική Συνεδρία | 20'", "Free Strategy Session | 20'")}
        </span>

        <h2 className="text-3xl md:text-4xl font-bold mb-4 tracking-tight leading-tight max-w-xl">
          {t(
            "Ξεκλειδώστε όλες τις δυνατότητες του EyeCaptain",
            "Unlock everything EyeCaptain can do for you"
          )}
        </h2>

        <p className="text-base md:text-lg text-muted-foreground mb-7 leading-relaxed max-w-lg">
          {t(
            "Σε 20 λεπτά θα δείτε ζωντανά κάθε ανάλυση και κάθε εργαλείο της πλατφόρμας, εφαρμοσμένα πάνω στο δικό σας website. Χωρίς πίεση πώλησης | μόνο πραγματικά insights που μπορείτε να αξιοποιήσετε από την επόμενη μέρα.",
            "In just 20 minutes you'll see every audit and capability of the platform applied live to your own website. No sales pressure | only actionable insights you can put to work the next day."
          )}
        </p>

        <ul className="grid sm:grid-cols-3 gap-3 mb-8 w-full max-w-xl text-left">
          {[
            t("Live demo σε όλες τις αναλύσεις", "Live demo of every audit"),
            t("Insights πάνω στο website σας", "Insights on your own website"),
            t("1-προς-1 με CRO expert", "1-on-1 with a CRO expert"),
          ].map((b) => (
            <li
              key={b}
              className="flex items-start gap-2 text-sm bg-muted/40 border border-border/60 rounded-xl p-3"
            >
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
              <span className="text-foreground/90">{b}</span>
            </li>
          ))}
        </ul>

        <Button
          size="lg"
          onClick={() => setBookingOpen(true)}
          className="h-14 px-8 text-base font-semibold rounded-xl shadow-lg shadow-primary/30 hover:shadow-primary/50 transition-shadow"
        >
          <CalendarDays className="mr-2 h-5 w-5" />
          {t("Κλείστε τη συνάντησή σας", "Book my strategy call")}

        </Button>

        <button
          onClick={onClose}
          className="mt-4 text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          {t("Όχι τώρα, ευχαριστώ", "No thanks, maybe later")}
        </button>
      </div>

      <WalkthroughBookingDialog
        open={bookingOpen}
        onOpenChange={setBookingOpen}
        language={language}
      />
    </div>
  );
}
